import { redirect, LoaderFunctionArgs } from "react-router-dom";
import { validateToken } from "utils/checkAuthorization";
import { getUserDetails } from "utils/localStorageServices";

const MEET_TOKEN_KEYS = ["meetToken", "token", "mt"];
const MEET_FLAG_KEYS = ["meet", "meetAddon", "isMeetAddon"];
const MEET_SOURCE_KEYS = ["source", "launchSource", "context"];
const MEET_SOURCE_VALUES = ["meet", "google-meet", "google_meet", "meet-addon"];

function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function hasMeetToken(url: URL) {
  return MEET_TOKEN_KEYS.some((key) => {
    const value = url.searchParams.get(key);
    return Boolean(value && value.trim());
  });
}

function hasMeetFlag(url: URL) {
  return MEET_FLAG_KEYS.some((key) => {
    const value = url.searchParams.get(key);
    return value === "1" || value === "true";
  });
}

function hasMeetSource(url: URL) {
  return MEET_SOURCE_KEYS.some((key) => {
    const value = (url.searchParams.get(key) || "").toLowerCase();
    return MEET_SOURCE_VALUES.includes(value);
  });
}

function hasMeetSdkParam(url: URL) {
  const value = url.searchParams.get("meet_sdk");
  return Boolean(value && value.trim());
}

function hasBypass() {
  if (process.env.NODE_ENV === "development") return true;
  return safeLocalStorageGet("meet_addon_dev_bypass") === "true";
}

function isMainStageRoute(url: URL) {
  return url.pathname === "/meet-addon/main-stage";
}

function isActivitySidePanelRoute(url: URL) {
  if (url.pathname !== "/meet-addon/side-panel") return false;
  return ["eventId", "eventViewerId", "presentationId", "activityId"].some((key) =>
    Boolean((url.searchParams.get(key) || "").trim()),
  );
}

/**
 * Guard for Meet add-on surfaces.
 * Allows valid Meet launches, with a dev fallback.
 *
 * The main stage must not require a Stagepanda auth token up-front. Meet opens
 * the same main-stage URL for participants after they click "Join activity",
 * and participants are expected to enter through the public /join/:eventId
 * flow instead of the app login flow.
 *
 * The side panel is still host-only unless it is an activity side-panel URL
 * containing launch metadata. Those URLs are allowed only so participants can
 * be redirected away from host controls.
 */
export function meetAddonGuardLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const allowed =
    hasMeetToken(url) ||
    hasMeetFlag(url) ||
    hasMeetSource(url) ||
    hasMeetSdkParam(url) ||
    hasBypass();

  if (!allowed) {
    const redirectTo = encodeURIComponent(`${url.pathname}${url.search}`);
    return redirect(`/login?redirect=${redirectTo}`);
  }

  if (isMainStageRoute(url) || isActivitySidePanelRoute(url)) {
    return null;
  }

  let isAuthenticated = false;
  try {
    const userDetails = getUserDetails();
    isAuthenticated = Boolean(userDetails?.token && validateToken());
  } catch {
    isAuthenticated = false;
  }
  if (isAuthenticated) return null;

  const redirectTo = encodeURIComponent(`${url.pathname}${url.search}`);
  return redirect(`/login?redirect=${redirectTo}`);
}
